import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Printer, X, FileDown, FileSpreadsheet, Mail, Loader2 } from 'lucide-react';
import { savePaperPdf, capturePaperPdfBase64 } from '@/utils/documentPdf';
import { EmailDocumentDialog } from './EmailDocumentDialog';

// ── Props ──────────────────────────────────────────────────────────────────────

/** Email defaults for the document; when supplied, the Email action is shown. */
export interface DocumentEmailConfig {
  documentType:    string;          // "GL Journal"
  documentNo?:     string | null;
  defaultTo?:      string[];
  defaultSubject?: string;
}

interface MasterReportPreviewProps {
  isOpen:    boolean;
  onClose:   () => void;
  title:     string;
  /** The formatted report content — use ReportHeader, ReportSection, etc. */
  children:  ReactNode;
  /** Base filename (no extension) for the PDF; defaults to the title. */
  fileBaseName?: string;
  /** When supplied, shows an Excel button that builds the workbook for this document. */
  onDownloadExcel?: () => void;
  /** When supplied, shows an Email button that opens the email composer. */
  email?: DocumentEmailConfig;
}

const PAPER_ID = 'master-report-paper';

// ── Component ─────────────────────────────────────────────────────────────────

export const MasterReportPreview = ({
  isOpen,
  onClose,
  title,
  children,
  fileBaseName,
  onDownloadExcel,
  email,
}: MasterReportPreviewProps) => {
  const [pdfBusy, setPdfBusy]     = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  // Inject @media print CSS that shows only the report region when printing
  useEffect(() => {
    if (!isOpen) return;

    const style = document.createElement('style');
    style.setAttribute('data-master-report-print', '');
    style.textContent = `
      @media print {
        body > *:not([data-master-report-root]) { display: none !important; }
        [data-master-report-root] {
          position: fixed !important;
          inset: 0 !important;
          background: white !important;
          z-index: 9999 !important;
          overflow: visible !important;
        }
        [data-report-no-print] { display: none !important; }
        @page { margin: 15mm; size: A4 portrait; }
      }
    `;
    document.head.appendChild(style);

    // Close on Escape
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);

    return () => {
      document.querySelector('[data-master-report-print]')?.remove();
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const baseName = fileBaseName || title.replace(/\s+/g, '-');
  const paperNode = () => document.getElementById(PAPER_ID);

  const handlePrint = () => window.print();

  const handlePdf = async () => {
    const node = paperNode();
    if (!node) return;
    setPdfBusy(true);
    try { await savePaperPdf(node, baseName); }
    finally { setPdfBusy(false); }
  };

  // Capture the paper as a PDF (base64) for the email attachment.
  const capturePdf = async () => {
    const node = paperNode();
    if (!node) return null;
    return capturePaperPdfBase64(node, baseName);
  };

  const btn = (bg: string): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '6px 14px', borderRadius: '6px',
    background: bg, color: 'white', border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: 600,
  });

  // Render via portal so the report root is a direct child of <body>
  return createPortal(
    <div data-master-report-root="" style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>

      {/* Screen overlay background */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
      }} />

      {/* Modal panel */}
      <div style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: '860px',
        margin: '0 auto',
      }}>

        {/* Toolbar — hidden when printing */}
        <div
          data-report-no-print=""
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            background: '#1e3a5f',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 600 }}>{title} — Preview</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" onClick={handlePrint} style={btn('#16a34a')}>
              <Printer size={14} /> Print
            </button>
            <button type="button" onClick={handlePdf} disabled={pdfBusy} style={{ ...btn('#0891b2'), opacity: pdfBusy ? 0.7 : 1 }}>
              {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} PDF
            </button>
            {onDownloadExcel && (
              <button type="button" onClick={onDownloadExcel} style={btn('#15803d')}>
                <FileSpreadsheet size={14} /> Excel
              </button>
            )}
            {email && (
              <button type="button" onClick={() => setEmailOpen(true)} style={btn('#4f46e5')}>
                <Mail size={14} /> Email
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                display: 'flex', alignItems: 'center',
                padding: '6px', borderRadius: '6px',
                background: 'rgba(255,255,255,0.15)', color: 'white',
                border: 'none', cursor: 'pointer',
              }}
              title="Close preview"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Scrollable report canvas */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 16px',
          background: '#e5e7eb',
        }}>
          {/* White paper */}
          <div
            id={PAPER_ID}
            style={{
              background: 'white',
              padding: '32px 40px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
              minHeight: '297mm',
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: '11px',
              color: '#111827',
              lineHeight: 1.5,
            }}
          >
            {children}
          </div>
        </div>
      </div>

      {email && (
        <EmailDocumentDialog
          isOpen={emailOpen}
          onClose={() => setEmailOpen(false)}
          documentType={email.documentType}
          documentNo={email.documentNo}
          defaultTo={email.defaultTo}
          defaultSubject={email.defaultSubject}
          capturePdf={capturePdf}
        />
      )}
    </div>,
    document.body,
  );
};
